import React, { useCallback, useEffect } from 'react';
import { Button, HStack, Box } from '@chakra-ui/react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Trans } from '@lingui/macro';
import Info from '../info/info';
import InfoFeedback from '../info-feedback/info-feedback';
import css from './footer-qtc.scss';
import { useValidate } from './hooks/use-validate';
import { resetConquestState, validateAnswer } from '../conquest/conquest.actions';
import { getQuestionSuccess, markActivity, nextActivity } from '../activity/activity-actions';
import { enableSpinner, disableSpinner } from '../mle/mle-actions';
import { LEARNOSITY_SPINNER } from '../../helpers/constants';
import { useBadStreak } from './hooks/use-bad-streak';
import { getValueFromUrl } from '../../helpers/utils';
import { ForceComplete } from '../force-complete/force-complete';

export const FooterQtc = () => {
  const dispatch = useDispatch();
  const { activityReducer, conquestReducer, mleReducer } = useSelector(state => state);
  const { questions, stepsCompleted } = activityReducer;
  const { isReady, validation } = conquestReducer;
  const { stepCount } = mleReducer || {};
  const { mutate: validate, isSuccess, data } = useValidate();
  const store = useStore();

  useBadStreak();

  useEffect(() => {
    if (isSuccess) {
      dispatch(disableSpinner(LEARNOSITY_SPINNER));
      dispatch(validateAnswer(data.data.validation.validationResult));
      dispatch(markActivity(data.data.validation.validationResult.isCorrect));
      dispatch(getQuestionSuccess(data.data));
    }
  }, [isSuccess]);

  const checkMyAnswerHandler = () => {
    dispatch(enableSpinner(LEARNOSITY_SPINNER));
    validate();
  };

  const nextHandler = useCallback(() => {
    if (getValueFromUrl('singleQuestionId')) {
      window.location.reload();
      return;
    }
    dispatch(resetConquestState());
    dispatch(getQuestionSuccess(data.data));
    dispatch(nextActivity());
  }, [data]);

  const saveCloseHandler = () => {
    if (window.history.length === 1) {
      window.close();
    } else {
      window.history.back();
    }
  };

  useEffect(() => {
    let enterPressed = false;
    const keyUpHandler = (event) => {
      if (event.key === 'Enter' && !enterPressed) {
        const state = store.getState();
        const currentIsReady = state.conquestReducer.isReady;
        const currentValidation = state.conquestReducer.validation;

        if (currentIsReady && !currentValidation) {
          event.preventDefault();
          checkMyAnswerHandler();
        } else if (currentValidation) {
          event.preventDefault();
          nextHandler();
        }
        enterPressed = true;
        setTimeout(() => { enterPressed = false; });
      }
    };
    window.addEventListener('keyup', keyUpHandler);
    return () => {
      window.removeEventListener('keyup', keyUpHandler);
    };
  }, [nextHandler, store]);

  return (
    <HStack className={css['footer-qtc']} >
      <HStack className={css['activity-side']} >
        <HStack className={css['info-stack']} spacing="20px">
          <Info />
          <InfoFeedback
            questions={questions}
          />
          <ForceComplete />
        </HStack>
        <Box>
          {
            validation ? (
              <Button
                variant="mleInvert"
                isDisabled={stepsCompleted === stepCount}
                onClick={nextHandler}
              >
                <Trans>Next</Trans>
              </Button>
            ) : (
              <Button
                variant="mleInvert"
                isDisabled={!isReady}
                onClick={checkMyAnswerHandler}
              >
                <Trans>Check my answer</Trans>
              </Button>
            )
          }
        </Box>
      </HStack>
      <Box className={css['progress-side']} >
        <Button
          variant="mle"
          onClick={saveCloseHandler}
        >
          <Trans>Save and close</Trans>
        </Button>
      </Box>
    </HStack>
  );
};
